// The taxi system's colour is set centrally (Layout:TaxiSystemSkins in Itf.BookingTerminal.Api) and spans
// everything from FST yellow to TT navy, so the app adapts to it rather than the other way round.

// Same fallback as ConfigController in the API
export const DEFAULT_BRAND_COLOR = "#252D33"

const WHITE = "#FFFFFF"
// --color-ink in style.css
const INK = "#111214"

// Accepts what the API actually sends: "#D7141C", "D7141C" (SAT has no hash), "#fff", or nothing
export function normalizeBrandColor(raw: string | null | undefined): string {
  const value = (raw ?? "").trim().replace(/^#?/, "#")
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toUpperCase()
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value
      .slice(1)
      .split("")
      .map(c => c + c)
      .join("")}`.toUpperCase()
  }
  return DEFAULT_BRAND_COLOR
}

function relativeLuminance(hex: string): number {
  const channels = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
  const [r, g, b] = channels.map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number]
  return (light + 0.05) / (dark + 0.05)
}

// Text on the brand colour: white or ink, whichever reads better
export function brandForeground(color: string): string {
  return contrastRatio(color, WHITE) >= contrastRatio(color, INK) ? WHITE : INK
}

// --- The app's own colours, bent toward the taxi system's ---
// Worked in OKLCH, where lightness is what the eye sees: a grey keeps its lightness (and so every contrast the design
// relies on) and only takes a little of the brand's hue.

type Oklch = { l: number; c: number; h: number }

function hexToLinear(hex: string): [number, number, number] {
  return [1, 3, 5].map(i => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
}

function toOklch(hex: string): Oklch {
  const [r, g, b] = hexToLinear(hex)
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_
  return { l: L, c: Math.hypot(A, B), h: (Math.atan2(B, A) * 180) / Math.PI }
}

// Linear sRGB, unclamped: a channel outside 0–1 means the colour is outside what a screen shows
function oklchToLinear({ l, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180)
  const B = c * Math.sin((h * Math.PI) / 180)
  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_
  ]
}

// The colour itself, with its chroma eased down until a screen can show it
function fromOklch(colour: Oklch): string {
  let c = colour.c
  let rgb = oklchToLinear({ ...colour, c })
  while (c > 0 && rgb.some(v => v < -0.0005 || v > 1.0005)) {
    c -= 0.002
    rgb = oklchToLinear({ ...colour, c: Math.max(0, c) })
  }
  return (
    "#" +
    rgb
      .map(v => {
        const x = Math.min(1, Math.max(0, v))
        const s = x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055
        return Math.round(s * 255)
          .toString(16)
          .padStart(2, "0")
      })
      .join("")
      .toUpperCase()
  )
}

// The greys and text colours of style.css by lightness, each with the chroma it may take of the brand's hue. Both
// themes set the same names, since they are set inline and would otherwise outlive a switch of theme. Surface stays
// pure white in light mode: it is the page itself, and what the logos are drawn for.
const NEUTRALS: Record<"light" | "dark", Record<string, [number, number]>> = {
  light: {
    ink: [0.18, 0.02],
    muted: [0.49, 0.022],
    faint: [0.64, 0.018],
    fill: [0.955, 0.008],
    line: [0.9, 0.01],
    raised: [1, 0],
    page: [0.93, 0.01],
    surface: [1, 0]
  },
  dark: {
    ink: [0.955, 0.008],
    muted: [0.76, 0.014],
    faint: [0.62, 0.014],
    fill: [0.255, 0.014],
    line: [0.315, 0.016],
    raised: [0.315, 0.016],
    page: [0.15, 0.012],
    surface: [0.205, 0.013]
  }
}

// The main action: the brand colour when it reads as a button on the page — in dark mode lifted until it does.
// A brand too pale for that (a yellow, a light green) leaves the main action to ink.
function mainAction(brand: Oklch, hex: string, dark: boolean): string | null {
  if (!dark) return contrastRatio(hex, WHITE) >= 3 ? hex : null
  const lifted = fromOklch({ ...brand, l: Math.max(brand.l, 0.74) })
  return contrastRatio(lifted, fromOklch({ l: 0.205, c: 0, h: 0 })) >= 3 ? lifted : null
}

// Every colour the app takes from the taxi system, as CSS custom properties for the theme in use
export function brandPalette(color: string, dark: boolean): Record<string, string> {
  const brand = toOklch(color)
  // A brand that is nearly grey itself (ITF's slate) gives its hue only in proportion
  const share = Math.min(1, brand.c / 0.08)
  const vars: Record<string, string> = {}
  for (const [name, [l, c]] of Object.entries(NEUTRALS[dark ? "dark" : "light"])) {
    vars[`--color-${name}`] = fromOklch({ l, c: c * share, h: brand.h })
  }
  const action = mainAction(brand, color, dark) ?? vars["--color-ink"]!
  vars["--color-hi"] = action
  vars["--color-on-hi"] = brandForeground(action)
  // The stripe and the fallback mark: the brand as it is, or lifted in dark mode so it does not vanish on near-black
  vars["--color-brand"] = dark ? fromOklch({ ...brand, l: Math.max(brand.l, 0.62) }) : color
  vars["--color-brand-fg"] = brandForeground(vars["--color-brand"])
  return vars
}
